import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  UserPlusIcon,
  TrashIcon,
  PencilSimpleIcon,
  CheckIcon,
  XIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatCircleTextIcon,
} from "@phosphor-icons/react";

export default function InterestsPanel({ carId }) {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", comment: "" });

  const load = useCallback(async () => {
    if (!carId) return;
    const r = await api.get(`/cars/${carId}/interests`);
    setItems(r.data);
  }, [carId]);

  useEffect(() => { load(); }, [load]);

  const open = (it = null) => {
    setEditing(it);
    setForm(it ? { name: it.name, phone: it.phone || "", email: it.email || "", comment: it.comment || "" } : { name: "", phone: "", email: "", comment: "" });
    setShow(true);
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) {
      toast.error("Συμπληρώστε ονοματεπώνυμο");
      return;
    }
    try {
      if (editing) {
        await api.put(`/cars/${carId}/interests/${editing.id}`, form);
        toast.success("Ενημερώθηκε");
      } else {
        await api.post(`/cars/${carId}/interests`, form);
        toast.success("Προστέθηκε");
      }
      setShow(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Διαγραφή ενδιαφέροντος;")) return;
    try {
      await api.delete(`/cars/${carId}/interests/${id}`);
      toast.success("Διαγράφηκε");
      load();
    } catch {
      toast.error("Αποτυχία");
    }
  };

  return (
    <div className="space-y-3" data-testid="interests-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">
            Πελάτες που εκδήλωσαν ενδιαφέρον
          </div>
          <div className="heading text-lg font-bold">{items.length} εγγραφές</div>
        </div>
        <button
          type="button"
          onClick={() => open()}
          className="btn-primary text-sm flex items-center gap-1.5"
          data-testid="add-interest-btn"
        >
          <UserPlusIcon size={16} /> Προσθήκη
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-black/20 p-8 text-center bg-[#F9FAFB]">
          <ChatCircleTextIcon size={32} weight="thin" className="mx-auto text-[#9CA3AF]" />
          <div className="text-sm text-[#4B5563] mt-2">
            Καμία καταχώρηση. Πατήστε «Προσθήκη» για να καταγράψετε πελάτη με ενδιαφέρον.
          </div>
        </div>
      ) : (
        <div className="border border-black/10 divide-y divide-black/5">
          {items.map((it) => (
            <div key={it.id} className="px-4 py-3 hover:bg-[#F9FAFB] group" data-testid={`interest-${it.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{it.name}</span>
                    {it.phone && (
                      <a href={`tel:${it.phone}`} className="text-xs font-mono text-[#4B5563] hover:text-[#0055FF] flex items-center gap-1">
                        <PhoneIcon size={11} /> {it.phone}
                      </a>
                    )}
                    {it.email && (
                      <a href={`mailto:${it.email}`} className="text-xs font-mono text-[#4B5563] hover:text-[#0055FF] flex items-center gap-1">
                        <EnvelopeIcon size={11} /> {it.email}
                      </a>
                    )}
                  </div>
                  {it.comment && (
                    <div className="mt-1.5 text-sm text-[#0A0A0A] bg-[#FEF3C7] border-l-2 border-[#F59E0B] px-2 py-1">
                      {it.comment}
                    </div>
                  )}
                  <div className="text-[10px] font-mono text-[#9CA3AF] mt-1.5">
                    {new Date(it.created_at).toLocaleString("el-GR")} · από {it.created_by || "—"}
                  </div>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <button onClick={() => open(it)} className="p-1.5 hover:bg-black/5" data-testid={`edit-interest-${it.id}`}>
                    <PencilSimpleIcon size={14} />
                  </button>
                  <button onClick={() => del(it.id)} className="p-1.5 hover:bg-[#E60000] hover:text-white" data-testid={`delete-interest-${it.id}`}>
                    <TrashIcon size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {show && createPortal((
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white border border-black/10 w-full max-w-md" data-testid="interest-modal">
            <div className="px-5 py-3 border-b border-black/10 flex items-center justify-between">
              <h3 className="heading font-bold">
                {editing ? "Επεξεργασία ενδιαφέροντος" : "Νέο ενδιαφέρον πελάτη"}
              </h3>
              <button type="button" onClick={() => setShow(false)} className="p-1 hover:bg-black/5">
                <XIcon size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <FormField label="Ονοματεπώνυμο *">
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="interest-name"
                  className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm"
                />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Τηλέφωνο">
                  <input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="interest-phone"
                    className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm font-mono"
                  />
                </FormField>
                <FormField label="Email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="interest-email"
                    className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm"
                  />
                </FormField>
              </div>
              <FormField label="Σχόλια / Σημειώσεις">
                <textarea
                  value={form.comment}
                  onChange={(e) => setForm({ ...form, comment: e.target.value })}
                  rows={4}
                  placeholder="π.χ. προτείνει €18000, θα ξανάρθει σαββατοκύριακο, ψάχνει αυτόματο..."
                  data-testid="interest-comment"
                  className="w-full px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm"
                />
              </FormField>
            </div>
            <div className="px-5 py-3 border-t border-black/10 bg-[#F9FAFB] flex justify-end gap-2">
              <button type="button" onClick={() => setShow(false)} className="btn-secondary">Άκυρο</button>
              <button type="submit" className="btn-primary flex items-center gap-1.5" data-testid="interest-save">
                <CheckIcon size={14} /> Αποθήκευση
              </button>
            </div>
          </form>
        </div>
      ), document.body)}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">{label}</span>
      {children}
    </label>
  );
}
